import { useEffect } from 'react';

import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ITrainingSetListResponse } from '../../api/types';
import { ITrainingSet } from '../../types/my-types';
import useGlynt from './useGlynt';

import { TRAINING_SET_PAGINATION } from '../../constants';

import { getOffsetFromUrl } from '../../utils/getOffsetFromUrl';

function mergeResults(data: InfiniteData<ITrainingSetListResponse> | undefined) {
  if (!data) return [];

  let results: ITrainingSet[] = [];

  data.pages.map((page) => {
    results = results.concat(page.results);
    return true;
  });

  return results;
}

// get all training sets from datapool
export default function useTrainingSets(
  enabled: boolean,
  quickView: boolean,
  search?: string,
  trained?: boolean,
  dataPoolId?: string
) {
  const glynt = useGlynt(dataPoolId);
  const cache = useQueryClient();

  const { data, isError, isLoading, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['allTrainingSets', { ...(dataPoolId ? { dataPoolId } : {}), ...(search ? { search } : {}) }],
      queryFn: async ({ pageParam }) => {
        return (await glynt).trainingSet.list({
          advanced: true,
          limit: TRAINING_SET_PAGINATION,
          offset: pageParam,
          ordering: 'label',

          // optional params:
          ...(quickView && { 'quick-view': 'true' }),
          ...(trained && { trained: true }),
          ...(search && { ilabel__contains: search }),
        });
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) return getOffsetFromUrl(lastPage.next);
      },
      refetchOnWindowFocus: false,
      enabled,
    });

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [data, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (search) {
      // Cancel queries if searching to reset offset
      const resetQueries = async () => await cache.cancelQueries({
        queryKey: ['allTrainingSets']
      });
      resetQueries();

      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]); // Only re-run when search changes, not when refetch reference changes

  // Keep previous results while fetching new ones to prevent flickering
  if (isLoading || isError || !enabled) {
    return [];
  }

  return mergeResults(data);
}
